import { useEffectivePermissions } from "@/features/authorization/hooks/useEffectivePermissions";
import type { ComponentProps } from "react";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  CalendarCheck,
  CalendarDays,
  CheckCircle2,
  Church,
  Clock3,
  TrendingUp,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { getAttendanceRecords } from "@/api/attendance";
import { getAttendanceOccurrences, type AttendanceOccurrence } from "@/api/attendance";
import { AnimatedNumber } from "@/components/ui/AnimatedNumber";
import { Card } from "@/components/ui/card";
import { LoadingPage } from "@/components/ui/LoadingPage";
import { QueryErrorState } from "@/components/ui/QueryErrorState";
import { EmptyState } from "@/components/ui/EmptyState";
import { PageContainer } from "@/design-system";
import { useAuth } from "@/features/auth/hooks/useAuth";
import type { AttendanceRecord } from "@/types/attendance";


export function AttendancePage() {
  const { churchId } = useAuth();

  const servicesQuery = useQuery({
    queryKey: ["attendance-occurrences", churchId],
    enabled: !!churchId,
    queryFn: () => getAttendanceOccurrences(churchId!),
  });

  const attendanceQuery = useQuery({
    queryKey: ["attendance-records", churchId, 1, 100],
    enabled: !!churchId,
    queryFn: () => getAttendanceRecords(churchId!, 1, 100),
  });

  if (servicesQuery.isLoading || attendanceQuery.isLoading) {
    return (
      <PageContainer>
        <LoadingPage />
      </PageContainer>
    );
  }

  if (
    servicesQuery.isError ||
    attendanceQuery.isError ||
    !servicesQuery.data ||
    !attendanceQuery.data
  ) {
    return (
      <PageContainer>
        <QueryErrorState
          title="Unable to load attendance"
          description="MinistryIQ could not load attendance information. Check your connection and try again."
          onRetry={() => {
            void servicesQuery.refetch();
            void attendanceQuery.refetch();
          }}
        />
      </PageContainer>
    );
  }

  const services = servicesQuery.data;
  const attendance = attendanceQuery.data.items;

  return (
    <AttendanceDashboard
      services={services}
      attendance={attendance}
      isRefreshing={
        servicesQuery.isFetching ||
        attendanceQuery.isFetching
      }
    />
  );
}

function AttendanceDashboard({
  services,
  attendance,
  isRefreshing,
}: {
  services: AttendanceOccurrence[];
  attendance: AttendanceRecord[];
  isRefreshing: boolean;
}) {
  const sortedServices = useMemo(
    () =>
      [...services].sort(
        (a, b) =>
          new Date(a.starts_at).getTime() -
          new Date(b.starts_at).getTime()
      ),
    [services]
  );

  if (services.length === 0) {
    return (
      <PageContainer>
        <AttendanceHero upcomingService={null} isRefreshing={isRefreshing} />
        <div className="mt-7">
          <EmptyState
            icon={<CalendarDays size={24} />}
            title="Create a service before recording attendance"
            description="Attendance is recorded against real service occurrences. Create a recurring service schedule, then return here to check people in."
            action={<AttendanceLink to="/services" className="inline-flex items-center gap-2 rounded-xl bg-indigo-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-400">Create First Service <ArrowRight size={16} /></AttendanceLink>}
          />
        </div>
      </PageContainer>
    );
  }

  const now = new Date();

  const upcomingServices = sortedServices
    .filter(
      (service) =>
        new Date(service.starts_at).getTime() >= now.getTime() &&
        service.status !== "CANCELLED"
    )
    .slice(0, 5);

  const recentServices = [...sortedServices]
    .filter(
      (service) =>
        new Date(service.starts_at).getTime() < now.getTime() &&
        service.status !== "CANCELLED"
    )
    .sort(
      (a, b) =>
        new Date(b.starts_at).getTime() -
        new Date(a.starts_at).getTime()
    )
    .slice(0, 6);

  const latestService = recentServices[0] ?? null;

  const latestServiceRecords = latestService
    ? attendance.filter(
        (record) =>
          record.service_occurrence_id === latestService.id
      )
    : [];

  const latestPresent = latestServiceRecords.filter(
    (record) => record.status === "PRESENT"
  ).length;

  const latestVisitors = latestServiceRecords.filter(
    (record) => record.status === "VISITOR"
  ).length;

  const totalPresent = recentServices.reduce(
    (total, service) =>
      total +
      attendance.filter(
        (record) =>
          record.service_occurrence_id === service.id &&
          record.status === "PRESENT"
      ).length,
    0
  );

  const averageCheckIns =
    recentServices.length > 0
      ? Math.round(totalPresent / recentServices.length)
      : 0;

  const chartData = buildAttendanceTrend(
    recentServices,
    attendance
  );

  return (
    <PageContainer>
      <AttendanceHero
        upcomingService={upcomingServices[0] ?? null}
        isRefreshing={isRefreshing}
      />

      <div className="mt-7 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          icon={<CalendarDays size={23} />}
          label="Eligible Services"
          value={recentServices.length}
          description="Recent past non-cancelled services"
          accentClassName="from-indigo-500 via-violet-500 to-fuchsia-400"
          iconClassName="bg-indigo-500/15 text-indigo-400"
        />

        <MetricCard
          icon={<CheckCircle2 size={23} />}
          label="Present"
          value={latestPresent}
          description="Most recent eligible service"
          accentClassName="from-emerald-500 via-teal-400 to-cyan-400"
          iconClassName="bg-emerald-500/15 text-emerald-400"
        />

        <MetricCard
          icon={<Users size={23} />}
          label="Visitors"
          value={latestVisitors}
          description="Most recent eligible service"
          accentClassName="from-sky-500 via-blue-500 to-indigo-400"
          iconClassName="bg-sky-500/15 text-sky-400"
        />

        <MetricCard
          icon={<TrendingUp size={23} />}
          label="Average Check-Ins"
          value={averageCheckIns}
          description="Across recent eligible services"
          accentClassName="from-amber-500 via-orange-400 to-yellow-300"
          iconClassName="bg-amber-500/15 text-amber-400"
        />
      </div>

      <div className="mt-7 grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
        <UpcomingServicesCard services={upcomingServices} />

        <QuickCheckInCard
          nextService={upcomingServices[0] ?? null}
        />
      </div>

      <div className="mt-7 grid gap-6 xl:grid-cols-[1.25fr_0.75fr]">
        <AttendanceTrendCard data={chartData} />

        <LatestServiceSummary
          service={latestService}
          present={latestPresent}
          visitors={latestVisitors}
        />
      </div>

    </PageContainer>
  );
}

function AttendanceHero({
  upcomingService,
  isRefreshing,
}: {
  upcomingService: AttendanceOccurrence | null;
  isRefreshing: boolean;
}) {
  return (
    <section className="relative overflow-hidden rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-900/95 via-slate-900/80 to-emerald-950/20 px-7 py-8 shadow-xl shadow-black/20">
      <div className="absolute -right-20 -top-24 h-64 w-64 rounded-full bg-emerald-500/10 blur-3xl" />
      <div className="absolute -bottom-28 left-24 h-56 w-56 rounded-full bg-indigo-500/5 blur-3xl" />

      <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-400">
              Attendance
            </p>

            {isRefreshing && (
              <p
                role="status"
                className="text-xs font-medium text-emerald-300"
              >
                Refreshing attendance...
              </p>
            )}
          </div>

          <h1 className="mt-3 text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Check in your church with confidence
          </h1>

          <p className="mt-3 max-w-2xl text-base leading-7 text-slate-300">
            Open a service roster, check in members quickly, and
            keep MinistryIQ intelligence up to date.
          </p>
        </div>

        {upcomingService ? (
          <AttendanceLink
            to={`/attendance/${upcomingService.id}`}
            className="group flex min-w-[280px] items-center justify-between gap-4 rounded-2xl border border-emerald-500/20 bg-emerald-500/10 p-5 transition hover:-translate-y-1 hover:border-emerald-500/40 hover:bg-emerald-500/15 hover:shadow-xl hover:shadow-emerald-950/20"
          >
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-emerald-400">
                Next Service
              </p>

              <p className="mt-2 font-semibold text-white">
                {upcomingService.title}
              </p>

              <p className="mt-1 text-sm text-slate-400">
                {formatDateTime(upcomingService.starts_at)}
              </p>
            </div>

            <ArrowRight className="text-emerald-400 transition group-hover:translate-x-1" />
          </AttendanceLink>
        ) : (
          <AttendanceLink
            to="/services"
            className="group flex min-w-[280px] items-center justify-between gap-4 rounded-2xl border border-slate-700 bg-slate-950/50 p-5 transition hover:-translate-y-1 hover:border-indigo-500/40"
          >
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-indigo-400">
                No Upcoming Service
              </p>

              <p className="mt-2 font-semibold text-white">
                Schedule a service
              </p>

              <p className="mt-1 text-sm text-slate-400">
                Create an occurrence before check-in.
              </p>
            </div>

            <ArrowRight className="text-indigo-400 transition group-hover:translate-x-1" />
          </AttendanceLink>
        )}
      </div>
    </section>
  );
}

function MetricCard({
  icon,
  label,
  value,
  description,
  accentClassName,
  iconClassName,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  description: string;
  accentClassName: string;
  iconClassName: string;
}) {
  return (
    <Card className="group relative overflow-hidden p-5 hover:-translate-y-2 hover:scale-[1.015] hover:shadow-2xl hover:shadow-indigo-950/20">
      <div
        className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${accentClassName}`}
      />

      <div
        className={`flex h-12 w-12 items-center justify-center rounded-xl transition duration-300 group-hover:scale-110 ${iconClassName}`}
      >
        {icon}
      </div>

      <p className="mt-5 text-sm font-medium text-slate-400">
        {label}
      </p>

      <AnimatedNumber
        value={value}
        className="mt-2 block text-4xl font-bold tracking-tight text-white"
      />

      <p className="mt-3 text-xs text-slate-600">
        {description}
      </p>
    </Card>
  );
}

function UpcomingServicesCard({
  services,
}: {
  services: AttendanceOccurrence[];
}) {
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between border-b border-slate-800 px-6 py-5">
        <div>
          <h2 className="text-xl font-semibold text-white">
            Upcoming Services
          </h2>

          <p className="mt-1 text-sm text-slate-500">
            Open a roster when check-in begins
          </p>
        </div>

        <AttendanceLink
          to="/services"
          className="text-sm font-medium text-indigo-400 hover:text-indigo-300"
        >
          View all
        </AttendanceLink>
      </div>

      {services.length === 0 ? (
        <div className="p-8 text-center">
          <Church className="mx-auto text-slate-600" />

          <p className="mt-3 font-medium text-white">
            No upcoming services
          </p>

          <p className="mt-2 text-sm text-slate-400">
            Create a service occurrence to begin attendance.
          </p>
        </div>
      ) : (
        <div className="divide-y divide-slate-800">
          {services.map((service) => (
            <AttendanceLink
              key={service.id}
              to={`/attendance/${service.id}`}
              className="group flex items-center justify-between gap-5 px-6 py-5 transition hover:bg-slate-800/40"
            >
              <div className="flex min-w-0 items-center gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-indigo-500/15 text-indigo-400 transition group-hover:scale-105">
                  <CalendarCheck size={21} />
                </div>

                <div className="min-w-0">
                  <p className="truncate font-semibold text-white">
                    {service.title}
                  </p>

                  <p className="mt-1 text-sm text-slate-400">
                    {formatDateTime(service.starts_at)}
                  </p>

                  <p className="mt-1 text-xs text-slate-600">
                    {formatServiceType(service.service_type)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <ServiceStatus status={service.status} />

                <ArrowRight className="h-4 w-4 text-slate-600 transition group-hover:translate-x-1 group-hover:text-indigo-400" />
              </div>
            </AttendanceLink>
          ))}
        </div>
      )}
    </Card>
  );
}

function QuickCheckInCard({
  nextService,
}: {
  nextService: AttendanceOccurrence | null;
}) {
  return (
    <Card className="relative overflow-hidden p-6">
      <div className="absolute -right-12 -top-12 h-36 w-36 rounded-full bg-emerald-500/10 blur-3xl" />

      <div className="relative">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-400">
          <CalendarCheck size={23} />
        </div>

        <h2 className="mt-5 text-xl font-semibold text-white">
          Quick Check-In
        </h2>

        <p className="mt-2 text-sm leading-6 text-slate-400">
          Launch the next service roster and begin checking in
          members immediately.
        </p>

        {nextService ? (
          <AttendanceLink
            to={`/attendance/${nextService.id}`}
            className="mt-6 flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-5 py-3 font-semibold text-white transition hover:-translate-y-0.5 hover:shadow-xl hover:shadow-emerald-950/30 active:translate-y-0"
          >
            Open Check-In
            <ArrowRight size={17} />
          </AttendanceLink>
        ) : (
          <AttendanceLink
            to="/services"
            className="mt-6 flex items-center justify-center gap-2 rounded-xl bg-slate-800 px-5 py-3 font-semibold text-white transition hover:bg-slate-700"
          >
            Manage Services
            <ArrowRight size={17} />
          </AttendanceLink>
        )}
      </div>
    </Card>
  );
}

function AttendanceTrendCard({
  data,
}: {
  data: {
    label: string;
    present: number;
  }[];
}) {
  return (
    <Card className="p-6">
      <div>
        <h2 className="text-xl font-semibold text-white">
          Attendance Trend
        </h2>

        <p className="mt-1 text-sm text-slate-500">
          Present check-ins across recent eligible services
        </p>
      </div>

      {data.length < 2 ? (
        <div className="mt-6 flex h-72 items-center justify-center rounded-2xl border border-dashed border-slate-700 bg-slate-950/40">
          <div className="text-center">
            <TrendingUp className="mx-auto text-slate-600" />

            <p className="mt-3 font-medium text-white">
              More history is needed
            </p>

            <p className="mt-2 text-sm text-slate-400">
              Attendance trends will appear after multiple services.
            </p>
          </div>
        </div>
      ) : (
        <div className="mt-6 h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={data}
              margin={{
                top: 12,
                right: 12,
                left: -18,
                bottom: 0,
              }}
            >
              <defs>
                <linearGradient
                  id="attendancePageGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor="#34d399"
                    stopOpacity={0.4}
                  />
                  <stop
                    offset="95%"
                    stopColor="#34d399"
                    stopOpacity={0.02}
                  />
                </linearGradient>
              </defs>

              <CartesianGrid
                stroke="#1e293b"
                strokeDasharray="4 4"
                vertical={false}
              />

              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                tick={{
                  fill: "#64748b",
                  fontSize: 12,
                }}
              />

              <YAxis
                allowDecimals={false}
                tickLine={false}
                axisLine={false}
                tick={{
                  fill: "#64748b",
                  fontSize: 12,
                }}
              />

              <Tooltip
                contentStyle={{
                  background: "#020617",
                  border: "1px solid #1e293b",
                  borderRadius: "14px",
                  color: "#fff",
                }}
              />

              <Area
                type="monotone"
                dataKey="present"
                name="Present"
                stroke="#34d399"
                strokeWidth={3}
                fill="url(#attendancePageGradient)"
                dot={{
                  r: 4,
                  fill: "#34d399",
                  strokeWidth: 0,
                }}
                activeDot={{
                  r: 6,
                  fill: "#a7f3d0",
                  stroke: "#064e3b",
                  strokeWidth: 2,
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

function LatestServiceSummary({
  service,
  present,
  visitors,
}: {
  service: AttendanceOccurrence | null;
  present: number;
  visitors: number;
}) {
  return (
    <Card className="overflow-hidden">
      <div className="border-b border-slate-800 px-6 py-5">
        <h2 className="text-xl font-semibold text-white">
          Latest Service
        </h2>

        <p className="mt-1 text-sm text-slate-500">
          Most recent eligible service summary
        </p>
      </div>

      {!service ? (
        <div className="p-8 text-center">
          <Clock3 className="mx-auto text-slate-600" />

          <p className="mt-3 font-medium text-white">
            No eligible service yet
          </p>
        </div>
      ) : (
        <div className="p-6">
          <p className="text-lg font-semibold text-white">
            {service.title}
          </p>

          <p className="mt-1 text-sm text-slate-500">
            {formatDateTime(service.starts_at)}
          </p>

          <div className="mt-6 grid grid-cols-2 gap-4">
            <MiniMetric
              label="Present"
              value={present}
              className="text-emerald-400"
            />

            <MiniMetric
              label="Visitors"
              value={visitors}
              className="text-sky-400"
            />
          </div>

          <AttendanceLink
            to={`/attendance/${service.id}`}
            className="mt-6 flex items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-950/50 px-4 py-3 text-sm font-semibold text-white transition hover:border-indigo-500/40 hover:bg-slate-800"
          >
            View Attendance
            <ArrowRight size={16} />
          </AttendanceLink>
        </div>
      )}
    </Card>
  );
}

function MiniMetric({
  label,
  value,
  className,
}: {
  label: string;
  value: string | number;
  className: string;
}) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-4">
      <p className={`text-2xl font-bold ${className}`}>
        {value}
      </p>

      <p className="mt-1 text-xs text-slate-500">
        {label}
      </p>
    </div>
  );
}

function ServiceStatus({
  status,
}: {
  status: string;
}) {
  const normalized = status.toUpperCase();

  const className =
    normalized === "COMPLETED"
      ? "bg-emerald-500/15 text-emerald-300"
      : normalized === "CANCELLED"
        ? "bg-rose-500/15 text-rose-300"
        : "bg-indigo-500/15 text-indigo-300";

  return (
    <span
      className={`rounded-full px-3 py-1 text-xs font-semibold ${className}`}
    >
      {formatServiceType(status)}
    </span>
  );
}

function buildAttendanceTrend(
  recentServices: AttendanceOccurrence[],
  attendance: AttendanceRecord[]
) {
  return [...recentServices]
    .reverse()
    .map((service) => ({
      label: new Date(service.starts_at).toLocaleDateString(
        undefined,
        {
          month: "short",
          day: "numeric",
        }
      ),
      present: attendance.filter(
        (record) =>
          record.service_occurrence_id === service.id &&
          record.status === "PRESENT"
      ).length,
    }));
}

function formatServiceType(value: string) {
  return value
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatDateTime(value: string) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}





function AttendanceLink(props: ComponentProps<typeof Link>) {
  const { permissions } = useEffectivePermissions();
  if (props.to === "/services" && !permissions.has("SERVICES_VIEW")) return null;
  return <Link {...props} />;
}
